import { useState } from 'react'
import { AlertTriangle, RefreshCw, Swords } from 'lucide-react'
import {
  useSyncSchedule,
  useExtendH2HFixtures,
  type SyncScheduleResult,
  type ExtendH2HFixturesResult,
} from '../queries'
import styles from './SeedRostersForm.module.css'

type SyncScheduleFormProps = {
  competitionId: string
}

export function SyncScheduleForm({ competitionId }: SyncScheduleFormProps) {
  const [season,     setSeason]     = useState(new Date().getFullYear())
  const [leagueId,   setLeagueId]   = useState('1')
  const [lastResult, setLastResult] = useState<SyncScheduleResult | null>(null)
  const [h2hResult,  setH2hResult]  = useState<ExtendH2HFixturesResult | null>(null)

  const sync      = useSyncSchedule()
  const extendH2H = useExtendH2HFixtures()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!leagueId.trim()) return
    sync.mutate(
      { competitionId, season, leagueId: leagueId.trim() },
      { onSuccess: (result) => setLastResult(result) },
    )
  }

  function handleGenerateH2H() {
    extendH2H.mutate(competitionId, { onSuccess: (result) => setH2hResult(result) })
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>2 · Importar Calendário via API-Football</h3>
      <p className={styles.hint}>
        Cria as rodadas e partidas a partir dos jogos da API-Football. Antes da fase
        eliminatória só existe a fase de grupos — <strong>rode novamente após cada sorteio</strong>{' '}
        para preencher os mata-matas. Requer times importados (passo 1).
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Temporada</span>
            <input
              type="number"
              className={styles.input}
              value={season}
              min={2020}
              max={2030}
              onChange={(e) => setSeason(Number(e.target.value))}
              disabled={sync.isPending}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>League ID (API-Football)</span>
            <input
              type="text"
              className={styles.input}
              value={leagueId}
              placeholder="ex: 1"
              onChange={(e) => setLeagueId(e.target.value)}
              disabled={sync.isPending}
            />
          </label>
        </div>

        <button type="submit" className={styles.btn} disabled={sync.isPending}>
          {sync.isPending ? <span className={styles.spinner} aria-hidden="true" /> : <RefreshCw size={14} />}
          {sync.isPending ? 'Importando…' : 'Importar Calendário'}
        </button>
      </form>

      {lastResult && <SyncScheduleResultView result={lastResult} />}

      <div className={styles.result}>
        <div className={styles.resultTitle}>Confrontos H2H</div>
        <p className={styles.hint}>
          Os confrontos das ligas H2H são fixados no 1º bloqueio, mas as rodadas eliminatórias
          só aparecem na API durante a competição. Depois de importar uma nova fase acima,
          gere os confrontos das rodadas recém-criadas. <strong>Idempotente</strong> — pode
          rodar quantas vezes quiser.
        </p>

        <button
          type="button"
          className={styles.btn}
          onClick={handleGenerateH2H}
          disabled={extendH2H.isPending}
        >
          {extendH2H.isPending ? <span className={styles.spinner} aria-hidden="true" /> : <Swords size={14} />}
          {extendH2H.isPending ? 'Gerando…' : 'Gerar Confrontos H2H'}
        </button>

        {h2hResult && (
          <div className={styles.actionStats}>
            <Stat label="Confrontos criados" value={h2hResult.fixturesCreated} accent />
            <Stat label="Ligas processadas"  value={h2hResult.leaguesProcessed} />
          </div>
        )}
      </div>
    </div>
  )
}

function SyncScheduleResultView({ result }: { result: SyncScheduleResult }) {
  return (
    <div className={styles.result}>
      <div className={styles.resultTitle}>Último resultado</div>
      <div className={styles.stats}>
        <Stat label="Fixtures na API" value={result.fixturesFromApi} muted />
        <Stat label="Rodadas criadas" value={result.gameweeksCreated} accent />
        <Stat label="Partidas criadas" value={result.matchesCreated} accent />
        <Stat label="Atualizadas"      value={result.matchesUpdated} />
      </div>

      {result.warnings.length > 0 && (
        <div className={styles.warnings}>
          <div className={styles.warningsHeader}>
            <AlertTriangle size={12} />
            {result.warnings.length} aviso{result.warnings.length === 1 ? '' : 's'}
          </div>
          <ul className={styles.warningsList}>
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function Stat({
  label, value, accent, muted,
}: {
  label:   string
  value:   number
  accent?: boolean
  muted?:  boolean
}) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={
        accent ? styles.statValueAccent :
        muted  ? styles.statValueMuted  :
        styles.statValue
      }>
        {value}
      </span>
    </div>
  )
}
